"use strict";

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __spreadArray = this && this.__spreadArray || function (to, from) {
  for (var i = 0, il = from.length, j = to.length; i < il; i++, j++) {
    to[j] = from[i];
  }

  return to;
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useCallAction = exports.useFetchAction = exports.useEvent = exports.useSubscription = void 0;

var react_1 = require("react");
/**
 * React Hook to subscribe to an specific event.
 *
 * @param {Event} event - The event to subscribe. The Event is considered constant across renders.
 * @param {Function} callback -  A function to be called when the subscription gets triggered.
 * @param {any[]} deps -  List of dependencies for the callback. Follow the same rules of useEffect.
 * @returns {void} - Void hook.
 */


function useSubscription(event, callback, deps) {
  if (deps === void 0) {
    deps = undefined;
  }

  var callbackRef = react_1.useRef(callback);
  callbackRef.current = callback;

  if (deps) {
    console.warn("Deps param has been deprecated, it is not useful anymore.");
  }

  react_1.useEffect(function () {
    var subscriptionCallback = function subscriptionCallback(eventData) {
      callbackRef.current(eventData);
    };

    var subscription = event.subscribe(subscriptionCallback);
    return function () {
      subscription.unsubscribe();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
}

exports.useSubscription = useSubscription;
/**
 * React Hook to subscribe to an Event.
 *
 * @param {Event} event - The event.
 * @param {object}  params - Params.
 * @param {object}  params.initialValue - Initial Value.
 * @param {Function}  params.reducer - Reducer for transform the data.
 * @returns {object} Data object.
 */

function useEvent(event, params) {
  var _a = params || {},
      reducer = _a.reducer,
      initialValue = _a.initialValue;

  var _b = react_1.useState(function () {
    if (initialValue && event.isEmpty()) {
      return initialValue;
    } else if (reducer) {
      return reducer(event.get());
    }

    return event.get();
  }),
      value = _b[0],
      setValue = _b[1];

  var reducerRef = react_1.useRef(params === null || params === void 0 ? void 0 : params.reducer);
  reducerRef.current = params === null || params === void 0 ? void 0 : params.reducer;
  react_1.useEffect(function () {
    var handleStateChange = function handleStateChange(state) {
      var eventState = reducerRef.current ? reducerRef.current(state) : state;
      setValue(eventState);
    };

    var subscription = event.subscribe(handleStateChange);
    return function () {
      subscription.unsubscribe();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
  return value;
}

exports.useEvent = useEvent;
/**
 * Hook that handle fetch promise actions, like querys to database.
 * This hook uses a declarative pattern.
 *
 * @param {ActionType} action - Action to fetch.
 * @param {Array} params - Param to call the action.
 * @param {UseFetchActionOptions} options - Option to handle the actions.
 * @param {Function} options.onCompleted - A callback to be called when the promise get resolved.
 * @param {Function} options.onError - A callback to be called when an error occurs.
 * @param {Function} options.skip - To skip the fetch ultil some validation happen.
 * @returns {UseFetchActionReturn} - Hook state result.
 */

function useFetchAction(action, params, options) {
  var _a = options || {},
      onCompleted = _a.onCompleted,
      onError = _a.onError,
      skip = _a.skip;

  var event = action.event,
      errorEvent = action.errorEvent;

  var _b = react_1.useState(function () {
    return {
      value: event.get(),
      loading: (options === null || options === void 0 ? void 0 : options.skip) ? false : true,
      error: null
    };
  }),
      _c = _b[0],
      value = _c.value,
      loading = _c.loading,
      error = _c.error,
      setState = _b[1];

  var callbacksRef = react_1.useRef({
    completed: onCompleted,
    error: onError
  });
  callbacksRef.current = {
    completed: onCompleted,
    error: onError
  };
  var fetch = react_1.useCallback(function () {
    setState(function (state) {
      return __assign(__assign({}, state), {
        loading: true
      });
    });
    action.apply(void 0, params); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, __spreadArray([action], params));
  react_1.useEffect(function () {
    if (skip) {
      return;
    }

    fetch();
  }, [fetch, skip]);
  react_1.useEffect(function () {
    var onSuccessCallback = function onSuccessCallback(data) {
      setState(function (state) {
        return __assign(__assign({}, state), {
          loading: false,
          value: data
        });
      });
      if (callbacksRef.current.completed) callbacksRef.current.completed(data);
    };

    var onErrorCallback = function onErrorCallback(data) {
      setState(function (state) {
        return __assign(__assign({}, state), {
          loading: false,
          error: data
        });
      });
      if (callbacksRef.current.error) callbacksRef.current.error(data);
    };

    var successSub = event.subscribe(onSuccessCallback);
    var errorSub = errorEvent.subscribe(onErrorCallback);
    return function () {
      successSub.unsubscribe();
      errorSub.unsubscribe();
    };
  }, [event, errorEvent]);
  return [value, loading, {
    error: error,
    refetch: fetch
  }];
}

exports.useFetchAction = useFetchAction;
/**
 * Hook that handle call promise actions, like mutations to database in a declarative way.
 *
 * @param {ActionType} action - The action to be called.
 * @param {UseActionOptions} options - Option to handle the actions.
 * @param {Function} options.onCompleted - A callback to be called when the promise get resolved.
 * @param {Function} options.onError - A callback to be called when an error occurs.
 * @returns {UseCallActionReturn} - Hook state result.
 */

function useCallAction(action, options) {
  var _a = options || {},
      onCompleted = _a.onCompleted,
      onError = _a.onError;

  var event = action.event,
      errorEvent = action.errorEvent;

  var _b = react_1.useState(function () {
    return {
      value: event.get(),
      loading: false,
      error: null
    };
  }),
      _c = _b[0],
      value = _c.value,
      loading = _c.loading,
      error = _c.error,
      setState = _b[1];

  var callbacksRef = react_1.useRef({
    completed: onCompleted,
    error: onError
  });
  callbacksRef.current = {
    completed: onCompleted,
    error: onError
  };
  var call = react_1.useCallback(function () {
    var params = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      params[_i] = arguments[_i];
    }

    setState(function (state) {
      return __assign(__assign({}, state), {
        loading: true
      });
    });
    action.apply(void 0, params);
  }, [action]);
  react_1.useEffect(function () {
    var onSuccessCallback = function onSuccessCallback(data) {
      setState(function (state) {
        return __assign(__assign({}, state), {
          loading: false,
          value: data
        });
      });
      if (callbacksRef.current.completed) callbacksRef.current.completed(data);
    };

    var onErrorCallback = function onErrorCallback(data) {
      setState(function (state) {
        return __assign(__assign({}, state), {
          loading: false,
          error: data
        });
      });
      if (callbacksRef.current.error) callbacksRef.current.error(data);
    };

    var successSub = event.subscribe(onSuccessCallback);
    var errorSub = errorEvent.subscribe(onErrorCallback);
    return function () {
      successSub.unsubscribe();
      errorSub.unsubscribe();
    };
  }, [event, errorEvent]);
  return [call, loading, {
    error: error,
    data: value
  }];
}

exports.useCallAction = useCallAction;